/**
 * Customer Management Table Configuration
 *
 * Centralized configuration for customer management table column widths,
 * default settings, and table-specific constants.
 *
 * Column Width Configuration:
 * - fixedWidth: The default width for the column
 * - minWidth: Minimum width when resizing (optional)
 * - maxWidth: Maximum width when resizing (optional, omit for expandable columns)
 *
 * Expandable columns (no maxWidth) will grow to fill available space.
 */

/**
 * Column width configuration for customer management table
 * Each column can specify fixedWidth, minWidth, and maxWidth
 *
 * @deprecated Column widths migrated to centralized registry (2025-10-24)
 * @see src/components/table/columns/registry.ts - EntityType 'customer'
 *
 * Use getColumnWidth('customer', columnId) from @/components/table/columns/registry instead.
 *
 * This constant is maintained temporarily for backward compatibility
 * and will be removed in a future cleanup phase.
 */
export const CUSTOMER_COLUMN_WIDTHS = {
  view: { fixedWidth: 40, minWidth: 40, maxWidth: 50 },
  status: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
  displayName: { fixedWidth: 370, minWidth: 200 }, // Expandable - no maxWidth
  type: { fixedWidth: 120, minWidth: 120, maxWidth: 130 },
  lastCompromiseDate: { fixedWidth: 120, minWidth: 100, maxWidth: 130 },
  critical: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
  high: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
  medium: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
  low: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
  info: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
} as const;

// ============================================================================
// COLUMN GROUPING & BEHAVIOR
// ============================================================================

/**
 * Columns that support sorting
 * Columns not in this list will have sorting disabled
 */
export const SORTABLE_COLUMNS = [
  'status',
  'displayName',
  'type',
  'lastCompromiseDate',
  'critical',
  'high',
  'medium',
  'low',
  'info',
] as const;

/**
 * Column ID that must always be visible (cannot be removed)
 */
export const REQUIRED_COLUMN_ID = 'displayName' as const;

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

/**
 * Default column order for customer management table
 *
 * Ordering convention:
 * 1. Status - Always first when present (user can remove or reorder)
 * 2. Core columns - displayName (required), type, health
 * 3. Risk severity columns - critical, high, medium, low, info
 *
 * Note: This is the DEFAULT order. Users have full control to reorder or remove any column
 * except 'displayName' which is required.
 * The 'actions' column is inherited from the Table component and should not be included here.
 */
export const DEFAULT_COLUMN_ORDER = [
  'status',
  'displayName', // Required: Cannot be removed
  'type',
  'lastCompromiseDate',
  'critical',
  'high',
  'medium',
  'low',
  'info',
] as const;

/**
 * Columns available but hidden by default
 */
export const HIDDEN_COLUMNS = [] as const;

// ============================================================================
// TABLE CONFIGURATION
// ============================================================================

/**
 * Complete customer table configuration
 */
export const CUSTOMER_TABLE_CONFIG = {
  widths: CUSTOMER_COLUMN_WIDTHS,
  defaultColumnOrder: DEFAULT_COLUMN_ORDER,
  hiddenColumns: HIDDEN_COLUMNS,
  storageKey: 'customer-management-v7',
  defaultRowHeight: 64,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a column supports sorting
 *
 * @param columnId - The column identifier to check
 * @returns True if the column is in the sortable columns list
 */
export const isColumnSortable = (columnId: string): boolean => {
  return SORTABLE_COLUMNS.includes(
    columnId as (typeof SORTABLE_COLUMNS)[number]
  );
};
